import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShieldCheck, Users, Receipt, Wallet, Store } from 'lucide-react';
import DashboardLayout, { NavCard } from '../../components/DashboardLayout';
import { usePerms } from '../../permissions/PermissionsContext';

/**
 * MerchantRevenuesHub — iter-264c (Feb 2026)
 * Groups every revenue-facing screen for the merchant platform:
 *   • إيرادات العمولة (new — links to the Commission Audit page which
 *     lists closed & finished requests with company + agent commissions)
 *   • سجل مدفوعات فواتير الوكلاء
 *   • إيرادات نظام الوكلاء والمدفوعات الحكومية والعمولة
 *   • إيرادات منصة العملاء للفواتير و الخدمات
 *   • إيرادات المتاجر
 */
export default function MerchantRevenuesHub() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const items = [
    {
      id: 'commission-revenue',
      titleAr: 'إيرادات العمولة',
      titleEn: 'Commission Revenues',
      descAr: 'التدقيق على العمولات المحصلة من الطلبات المغلقة والمنتهية',
      descEn: 'Audit collected commissions from closed & finished requests',
      icon: ShieldCheck,
      route: '/dashboard/finance/cloudgm/merchants/commission-revenue',
      color: '#eab308',
      screenCode: 'FIN-MERCH-COMM-REV',
      testId: 'card-commission-revenue',
    },
    {
      id: 'agent-payments',
      titleAr: 'سجل مدفوعات فواتير الوكلاء',
      titleEn: 'Agent Invoice Payment Records',
      descAr: 'سجل شامل بفواتير الوكلاء والدفع من المحفظة (خصم كوميشن)',
      descEn: 'Complete ledger of agent invoices — link, bank, and wallet (commission) payments',
      icon: Users,
      route: '/dashboard/finance/cloudgm/merchants/agent-payments',
      color: '#a855f7',
      screenCode: 'CGM-AGENT-PAY-01',
      testId: 'card-agent-payment-records',
    },
    {
      id: 'tax-invoices',
      titleAr: 'إيرادات نظام الوكلاء والمدفوعات الحكومية والعمولة',
      titleEn: 'Agent System Revenue, Gov Fees & Commission',
      descAr: 'جدول موحد بالإيرادات، الرسوم الحكومية، عمولة الوكيل وصافي الربح',
      descEn: 'Unified ledger — revenue, gov fees, agent commission & net profit',
      icon: Receipt,
      route: '/dashboard/finance/cloudgm/merchants/tax-invoices',
      color: '#d4af37',
      screenCode: 'FIN-MERCH-TAX-01',
      testId: 'card-tax-invoices',
    },
    {
      id: 'payments',
      titleAr: 'إيرادات منصة العملاء للفواتير و الخدمات',
      titleEn: 'Customer Platform Revenue — Invoices & Services',
      descAr: 'سجل شامل بكل الفواتير والمدفوعات المُنشأة لمنصة التجار',
      descEn: 'Complete ledger of all invoices and payments created for the merchant platform',
      icon: Wallet,
      route: '/dashboard/finance/cloudgm/merchants/payments',
      color: '#3B9EDC',
      screenCode: 'CGM-MERCH-FIN-01',
      testId: 'card-payment-records',
    },
    {
      id: 'store-revenues',
      titleAr: 'إيرادات المتاجر',
      titleEn: 'Store Revenues',
      descAr: 'سجل شامل بإيرادات كل متجر إلكتروني وحالة محفظة التاجر',
      descEn: 'Comprehensive ledger of every e-commerce store with wallet routing',
      icon: Store,
      route: '/dashboard/finance/cloudgm/merchants/store-revenues',
      color: '#38bdf8',
      screenCode: 'FIN-MERCH-STORE-REV',
      testId: 'card-store-revenues',
    },
  ];

  const containerVariants = {
    hidden:  { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
  };

  return (
    <DashboardLayout
      title="الإيرادات"
      titleEn="Revenues"
      subtitle="عرض كل إيرادات منصة التجار — العمولات، الوكلاء، الرسوم والمتاجر"
      subtitleEn="View every merchant-platform revenue stream in one place"
      screenCode="FIN-CGM-MERCH-REV"
      backPath="/dashboard/finance/cloudgm/merchants"
      breadcrumb={[
        { labelAr: 'المالية',                labelEn: 'Finance',                     path: '/dashboard/finance' },
        { labelAr: 'CLOUDGM FZ LLC',         labelEn: 'CLOUDGM FZ LLC',              path: '/dashboard/finance/cloudgm' },
        { labelAr: 'منصة التجار (المالية)',  labelEn: 'Merchant Platform (Finance)', path: '/dashboard/finance/cloudgm/merchants' },
        { labelAr: 'الإيرادات',              labelEn: 'Revenues',                    path: null },
      ]}
    >
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {visiblePages(items, 'route').map((item) => (
            <NavCard
              key={item.id}
              icon={item.icon}
              title={item.titleAr}
              titleEn={item.titleEn}
              description={item.descAr}
              descriptionEn={item.descEn}
              onClick={() => navigate(item.route)}
              color={item.color}
              screenCode={item.screenCode}
              testId={item.testId}
            />
          ))}
        </motion.div>
      </section>
    </DashboardLayout>
  );
}
